import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Download,
  Info,
  Loader2,
  PhoneIncoming,
  RefreshCw,
  Search,
  TrendingUp,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import * as XLSX from "xlsx";

// ── Types ─────────────────────────────────────────────────────────────────────

interface CustomerOption {
  id: string;
  name: string;
  queueNames: string[];
  forecastStartDate: string | null;
  hasEstimates: boolean;
}

interface ForecastDay {
  weekday: string;
  date: string;
  callsReceived: number;
  answeredCalls: number;
  ahtMinutes: number;
  pccHours: number;
  isEstimated: boolean;
}

interface ForecastWeek {
  weekIndex: number;
  label: string;
  startDate: string;
  endDate: string;
  isEstimated: boolean;
  hasPartialEstimates: boolean;
  days: ForecastDay[];
}

interface ForecastWeightedAverage {
  weekday: string;
  weightedCalls: number;
  weightedAHT: number;
  weightedPCCHours: number;
}

interface QueueWeightedAverage {
  queueName: string;
  weightedAverages: ForecastWeightedAverage[];
}

interface ForecastProjection {
  customer: {
    id: string;
    name: string;
    queueNames: string[];
    forecastStartDate: string | null;
    hasEstimates: boolean;
  };
  period: {
    asOfDate: string;
    startDate: string;
    endDate: string;
    timezone: string;
    weeks: number;
  };
  weekly: ForecastWeek[];
  weightedAverages: ForecastWeightedAverage[];
  queueWeightedAverages: QueueWeightedAverage[];
}

const SUN_TO_SAT = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt1 = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmt2 = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt3 = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

// Mirror the backend WEEK_WEIGHTS to show the weight applied to each week
const BACKEND_WEEK_WEIGHTS = [0.03, 0.05, 0.07, 0.08, 0.12, 0.15, 0.20, 0.30];
function getWeekWeightPercent(weekIndex: number, totalWeeks: number): number {
  let weights: number[];
  if (totalWeeks <= 0) return 0;
  if (totalWeeks < BACKEND_WEEK_WEIGHTS.length) {
    const slice = BACKEND_WEEK_WEIGHTS.slice(BACKEND_WEEK_WEIGHTS.length - totalWeeks);
    const total = slice.reduce((s, w) => s + w, 0);
    weights = total > 0 ? slice.map((w) => w / total) : slice.map(() => 1 / totalWeeks);
  } else if (totalWeeks > BACKEND_WEEK_WEIGHTS.length) {
    weights = Array.from({ length: totalWeeks }, () => 1 / totalWeeks);
  } else {
    weights = BACKEND_WEEK_WEIGHTS;
  }
  const weightIndex = totalWeeks - 1 - weekIndex;
  return weightIndex >= 0 && weightIndex < weights.length ? weights[weightIndex] : 0;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function BudgetingForecasting() {
  const today = format(new Date(), "yyyy-MM-dd");

  // Controls
  const [asOfDate, setAsOfDate] = useState(today);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Data
  const [projections, setProjections] = useState<ForecastProjection[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProjection, setLoadingProjection] = useState(false);

  // ── Load customers ──────────────────────────────────────────────────────────

  useEffect(() => {
    setLoadingCustomers(true);
    fetch("/api/v1/forecasting/customers", {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then((r) => r.json())
      .then((d) => setCustomers(d.data || []))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoadingCustomers(false));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  // ── Load projections ────────────────────────────────────────────────────────

  const loadProjections = useCallback(
    async (ids: string[], date: string) => {
      if (ids.length === 0) {
        setProjections([]);
        return;
      }
      setLoadingProjection(true);
      setProjections([]);
      try {
        const results: ForecastProjection[] = [];
        for (const id of ids) {
          const params = new URLSearchParams({ customerId: id });
          if (date) params.set("asOfDate", date);
          const res = await fetch(`/api/v1/forecasting/projection?${params}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || "Failed to load projection");
          }
          const data = await res.json();
          results.push(data.data);
        }
        setProjections(results);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load projections");
      } finally {
        setLoadingProjection(false);
      }
    },
    []
  );

  useEffect(() => {
    loadProjections(selectedIds, asOfDate);
  }, [selectedIds, asOfDate, loadProjections]);

  // ── Customer selector helpers ───────────────────────────────────────────────

  const filteredCustomers = customers.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleCustomer = (id: string) => {
    const customer = customers.find((c) => c.id === id);
    if (customer && !customer.forecastStartDate) {
      toast.error(`"${customer.name}" has no forecast start date set. Please configure it in Customer Profiles first.`);
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedCustomers = customers.filter((c) => selectedIds.includes(c.id));

  // ── Excel Export ─────────────────────────────────────────────────────────────

  const downloadExcel = () => {
    if (projections.length === 0) return;

    // Header row
    const header = ["Customer / Queue / Metric", ...SUN_TO_SAT];
    const rows: (string | number)[][] = [header];

    for (const proj of projections) {
      const { customer, weightedAverages, queueWeightedAverages } = proj;

      // Helper: get day values in Sun→Sat order for a set of weighted averages
      const dayValues = (
        avgs: { weekday: string; weightedCalls: number; weightedAHT: number; weightedPCCHours: number }[]
      ) =>
        SUN_TO_SAT.map((day) => {
          const d = avgs.find((a) => a.weekday === day);
          return d ?? { weightedCalls: 0, weightedAHT: 0, weightedPCCHours: 0 };
        });

      const custDays = dayValues(weightedAverages);

      // Customer summary row — show weighted calls per day
      rows.push([
        customer.name,
        ...custDays.map((d) => Math.round(d.weightedCalls * 10) / 10),
      ]);

      // One block per queue
      for (const queueName of customer.queueNames) {
        const queueData = queueWeightedAverages?.find((q) => q.queueName === queueName);
        const qDays = queueData ? dayValues(queueData.weightedAverages) : SUN_TO_SAT.map(() => ({ weightedCalls: 0, weightedAHT: 0, weightedPCCHours: 0 }));

        // Queue header row (indented with spaces)
        rows.push([
          `  ${queueName}`,
          ...qDays.map((d) => Math.round(d.weightedCalls * 10) / 10),
        ]);

        // Calls Received
        rows.push([
          `    Calls Received`,
          ...qDays.map((d) => Math.round(d.weightedCalls * 10) / 10),
        ]);

        // AHT (min)
        rows.push([
          `    AHT (min)`,
          ...qDays.map((d) => Math.round(d.weightedAHT * 100) / 100),
        ]);

        // PCC Hours
        rows.push([
          `    PCC Hours`,
          ...qDays.map((d) => Math.round(d.weightedPCCHours * 1000) / 1000),
        ]);
      }

      // ── 8-Week History section ──
      rows.push(["  8-Week History", ...SUN_TO_SAT.map(() => "")]);

      for (const week of proj.weekly) {
        const daysBySunSat = SUN_TO_SAT.map((dayName) =>
          week.days.find((d) => d.weekday === dayName) ?? null
        );

        const estLabel = week.isEstimated
          ? " (Est)"
          : week.hasPartialEstimates
          ? " (Partial Est)"
          : "";

        // Week header — calls per day
        const weekWeightPct = Math.round(getWeekWeightPercent(week.weekIndex, proj.weekly.length) * 100);
        rows.push([
          `    Week ${week.weekIndex + 1} [${weekWeightPct}%] — ${week.label}${estLabel}`,
          ...daysBySunSat.map((d) => (d ? d.callsReceived : 0)),
        ]);

        // Calls Received
        rows.push([
          `      Calls Received`,
          ...daysBySunSat.map((d) => (d ? d.callsReceived : 0)),
        ]);

        // AHT (min)
        rows.push([
          `      AHT (min)`,
          ...daysBySunSat.map((d) =>
            d ? Math.round(d.ahtMinutes * 100) / 100 : 0
          ),
        ]);

        // PCC Hours
        rows.push([
          `      PCC Hours`,
          ...daysBySunSat.map((d) =>
            d ? Math.round(d.pccHours * 1000) / 1000 : 0
          ),
        ]);
      }

      // Blank separator row between customers
      rows.push(["", ...SUN_TO_SAT.map(() => "")]);
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Column widths: first col wide, day cols narrower
    ws["!cols"] = [
      { wch: 42 },
      ...SUN_TO_SAT.map(() => ({ wch: 14 })),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Forecast");

    const fileName = `forecast-${asOfDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-blue-600" />
            Forecasting
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            8-week weighted averages — Calls Received, AHT, and PCC Hours per day of week
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadExcel}
            disabled={projections.length === 0}
            className="flex items-center gap-2 rounded-md border border-green-600 px-4 py-2 text-sm font-medium text-green-700 bg-white hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            Download Excel
          </button>
          <button
            onClick={() => loadProjections(selectedIds, asOfDate)}
            disabled={loadingProjection || selectedIds.length === 0}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loadingProjection ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-wrap gap-6 items-end">
          {/* As Of Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <CalendarClock className="inline h-4 w-4 mr-1 text-gray-400" />
              As Of Date
            </label>
            <input
              type="date"
              value={asOfDate}
              max={today}
              onChange={(e) => setAsOfDate(e.target.value || today)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Trailing 8 weeks ending on the Sunday before this date</p>
          </div>

          {/* Customer Selector */}
          <div className="flex-1 min-w-[240px]" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer{selectedIds.length > 0 && ` (${selectedIds.length} selected)`}
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">
                    {selectedIds.length === 0
                      ? "Select customers…"
                      : `${selectedIds.length} customer${selectedIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute z-20 mt-1 w-full max-w-sm rounded-md border border-gray-200 bg-white shadow-lg">
                  <div className="p-2">
                    <div className="mb-2 flex items-center gap-2 border-b pb-2">
                      <Search className="h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search customers…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 border-0 px-1 py-0.5 text-sm focus:outline-none"
                      />
                      {searchQuery && (
                        <button onClick={() => setSearchQuery("")}>
                          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                    {!loadingCustomers && filteredCustomers.length > 0 && (
                      <div className="mb-1 flex items-center justify-between px-1 pb-1 border-b">
                        <span className="text-xs text-gray-500">
                          {filteredCustomers.filter((c) => selectedIds.includes(c.id)).length} / {filteredCustomers.length} selected
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const configurableIds = filteredCustomers.filter((c) => !!c.forecastStartDate).map((c) => c.id);
                            const allSelected = configurableIds.every((id) => selectedIds.includes(id));
                            if (allSelected) {
                              setSelectedIds((prev) => prev.filter((id) => !configurableIds.includes(id)));
                            } else {
                              setSelectedIds((prev) => [...new Set([...prev, ...configurableIds])]);
                            }
                          }}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800"
                        >
                          {filteredCustomers.filter((c) => !!c.forecastStartDate).every((c) => selectedIds.includes(c.id)) ? "Clear All" : "Select All"}
                        </button>
                      </div>
                    )}
                    {loadingCustomers ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      </div>
                    ) : (
                      <div className="max-h-56 overflow-y-auto">
                        {filteredCustomers.map((c) => {
                          const hasConfig = !!c.forecastStartDate;
                          return (
                            <label
                              key={c.id}
                              className={`flex items-center gap-2 px-3 py-2 ${hasConfig ? "hover:bg-gray-50 cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                              title={!hasConfig ? "No forecast start date configured" : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(c.id)}
                                onChange={() => toggleCustomer(c.id)}
                                disabled={!hasConfig}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                              />
                              <span className={`text-sm ${hasConfig ? "text-gray-700" : "text-gray-400"}`}>{c.name}</span>
                              {!hasConfig && (
                                <span className="ml-auto text-xs text-amber-500 font-medium">No config</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Selected chips */}
            {selectedCustomers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {selectedCustomers.map((c) => (
                  <span
                    key={c.id}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                  >
                    {c.name}
                    <button onClick={() => toggleCustomer(c.id)}>
                      <X className="h-3 w-3 text-blue-600 hover:text-blue-800" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loadingProjection && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading projections…</span>
        </div>
      )}

      {/* Empty state */}
      {!loadingProjection && selectedIds.length === 0 && (
        <div className="rounded-lg bg-white p-12 shadow text-center">
          <PhoneIncoming className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">Select one or more customers above to view their forecast.</p>
        </div>
      )}

      {/* Projections */}
      {!loadingProjection && projections.length > 0 && (
        <ForecastPivotTable projections={projections} />
      )}
    </div>
  );
}

// ── ForecastPivotTable ────────────────────────────────────────────────────────

function ForecastPivotTable({ projections }: { projections: ForecastProjection[] }) {
  const [expandedCustomers, setExpandedCustomers] = useState<Record<string, boolean>>({});
  const [expandedQueues, setExpandedQueues] = useState<Record<string, boolean>>({});

  return (
    <div className="rounded-lg bg-white shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-[220px] border-r border-gray-200">
                Customer / Queue / Metric
              </th>
              {SUN_TO_SAT.map((day) => (
                <th
                  key={day}
                  className="px-3 py-3 text-center text-xs font-semibold text-gray-600 min-w-[90px]"
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {projections.map((proj) => (
              <CustomerRows
                key={proj.customer.id}
                projection={proj}
                expanded={!!expandedCustomers[proj.customer.id]}
                onToggle={() =>
                  setExpandedCustomers((prev) => ({
                    ...prev,
                    [proj.customer.id]: !prev[proj.customer.id],
                  }))
                }
                expandedQueues={expandedQueues}
                onToggleQueue={(key) =>
                  setExpandedQueues((prev) => ({ ...prev, [key]: !prev[key] }))
                }
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
        8-week weighted averages · Click a customer to expand queues · Click a queue to see metrics
      </div>
    </div>
  );
}

// ── CustomerRows ──────────────────────────────────────────────────────────────

function CustomerRows({
  projection,
  expanded,
  onToggle,
  expandedQueues,
  onToggleQueue,
}: {
  projection: ForecastProjection;
  expanded: boolean;
  onToggle: () => void;
  expandedQueues: Record<string, boolean>;
  onToggleQueue: (key: string) => void;
}) {
  const { customer, weekly, weightedAverages, queueWeightedAverages } = projection;
  const estimatedWeeks = weekly.filter((w) => w.isEstimated || w.hasPartialEstimates).length;

  // Local expansion state for 8-week history (one toggle per queue)
  const [expandedWeekHistories, setExpandedWeekHistories] = useState<Record<string, boolean>>({});
  const [expandedWeeks, setExpandedWeeks] = useState<Record<string, boolean>>({});

  const customerDayValues = SUN_TO_SAT.map(
    (day) =>
      weightedAverages.find((w) => w.weekday === day) ?? {
        weekday: day,
        weightedCalls: 0,
        weightedAHT: 0,
        weightedPCCHours: 0,
      }
  );

  const totalCalls = weightedAverages.reduce((s, d) => s + d.weightedCalls, 0);
  const totalPCC = weightedAverages.reduce((s, d) => s + d.weightedPCCHours, 0);
  const totalMinutes = weightedAverages.reduce((s, d) => s + d.weightedCalls * d.weightedAHT, 0);

  return (
    <>
      {/* ── Customer header row ── */}
      <tr
        className="border-t-2 border-gray-300 cursor-pointer select-none bg-blue-50 hover:bg-blue-100 transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3 sticky left-0 bg-blue-50 z-10 border-r border-blue-200">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-blue-500 flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-blue-500 flex-shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{customer.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {customer.queueNames.length} queue{customer.queueNames.length !== 1 ? "s" : ""}
                {" · "}
                <span className="text-blue-600">{fmt1(totalCalls)} calls/wk</span>
                {" · "}
                <span className="text-teal-600">{fmt3(totalMinutes)} min/wk</span>
                {" · "}
                <span className="text-purple-600">{fmt2(totalPCC)} PCC hrs/wk</span>
                {estimatedWeeks > 0 && (
                  <span className="ml-2 text-amber-600">
                    <Info className="inline h-3 w-3 mr-0.5" />
                    {estimatedWeeks}/8 wks estimated
                  </span>
                )}
              </p>
            </div>
          </div>
        </td>
        {customerDayValues.map((val, i) => (
          <td key={i} className="px-2 py-2 text-center">
            <span className="block text-sm font-semibold text-blue-700 leading-tight">
              {fmt1(val.weightedCalls)}
            </span>
            <div className="mt-1 space-y-0.5">
              <div className="flex items-center justify-center gap-1">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">AHT</span>
                <span className="text-[10px] text-gray-500">{fmt2(val.weightedAHT)}</span>
              </div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-[10px] font-medium text-teal-500 uppercase tracking-wide">Min</span>
                <span className="text-[10px] text-teal-600 font-medium">{fmt1(val.weightedCalls * val.weightedAHT)}</span>
              </div>
              <div className="flex items-center justify-center gap-1">
                <span className="text-[10px] font-medium text-purple-400 uppercase tracking-wide">PCC hrs</span>
                <span className="text-[10px] text-purple-500">{fmt3(val.weightedPCCHours)}</span>
              </div>
            </div>
          </td>
        ))}
      </tr>

      {/* ── Queue rows (visible when customer is expanded) ── */}
      {expanded &&
        customer.queueNames.map((queueName) => {
          const queueKey = `${customer.id}::${queueName}`;
          const isQueueExpanded = !!expandedQueues[queueKey];
          const queueData = queueWeightedAverages?.find((q) => q.queueName === queueName);

          const queueDayValues = SUN_TO_SAT.map(
            (day) =>
              queueData?.weightedAverages.find((w) => w.weekday === day) ?? {
                weekday: day,
                weightedCalls: 0,
                weightedAHT: 0,
                weightedPCCHours: 0,
              }
          );

          return (
            <Fragment key={queueKey}>
              {/* Queue row */}
              <tr
                className="border-t border-gray-100 cursor-pointer select-none bg-white hover:bg-gray-50 transition-colors"
                onClick={() => onToggleQueue(queueKey)}
              >
                <td className="px-4 py-2 pl-9 sticky left-0 bg-white z-10 border-r border-gray-100">
                  <div className="flex items-center gap-1.5">
                    {isQueueExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-xs font-medium text-gray-700 truncate">{queueName}</span>
                  </div>
                </td>
                {queueDayValues.map((val, i) => (
                  <td key={i} className="px-3 py-2 text-center text-xs text-gray-500">
                    {val.weightedCalls > 0 ? (
                      fmt1(val.weightedCalls)
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Metric rows (visible when queue is expanded) */}
              {isQueueExpanded && (
                <>
                  {/* Calls Received */}
                  <tr className="border-t border-gray-50 bg-gray-50">
                    <td className="px-4 py-1.5 pl-16 sticky left-0 bg-gray-50 z-10 text-xs text-gray-500 border-r border-gray-100">
                      Calls Received
                    </td>
                    {queueDayValues.map((val, i) => (
                      <td key={i} className="px-3 py-1.5 text-center text-xs text-gray-700">
                        {fmt1(val.weightedCalls)}
                      </td>
                    ))}
                  </tr>
                  {/* AHT */}
                  <tr className="border-t border-gray-50 bg-gray-50">
                    <td className="px-4 py-1.5 pl-16 sticky left-0 bg-gray-50 z-10 text-xs text-gray-500 border-r border-gray-100">
                      AHT (min)
                    </td>
                    {queueDayValues.map((val, i) => (
                      <td key={i} className="px-3 py-1.5 text-center text-xs text-gray-700">
                        {fmt2(val.weightedAHT)}
                      </td>
                    ))}
                  </tr>
                  {/* PCC Hours */}
                  <tr className="border-t border-gray-50 bg-gray-50">
                    <td className="px-4 py-1.5 pl-16 sticky left-0 bg-gray-50 z-10 text-xs text-gray-500 border-r border-gray-100">
                      PCC Hours
                    </td>
                    {queueDayValues.map((val, i) => (
                      <td key={i} className="px-3 py-1.5 text-center text-xs font-medium text-purple-700">
                        {fmt3(val.weightedPCCHours)}
                      </td>
                    ))}
                  </tr>

                  {/* ── 8-Week History toggle ── */}
                  <tr
                    className="border-t border-dashed border-gray-200 cursor-pointer hover:bg-gray-100 bg-gray-50"
                    onClick={() =>
                      setExpandedWeekHistories((prev) => ({
                        ...prev,
                        [queueKey]: !prev[queueKey],
                      }))
                    }
                  >
                    <td className="px-4 py-1.5 pl-16 sticky left-0 bg-gray-50 z-10 border-r border-gray-100">
                      <div className="flex items-center gap-1.5">
                        {expandedWeekHistories[queueKey] ? (
                          <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                        )}
                        <span className="text-xs text-gray-400 italic">8-Week History</span>
                      </div>
                    </td>
                    {SUN_TO_SAT.map((_, i) => <td key={i} />)}
                  </tr>

                  {/* ── Week rows ── */}
                  {expandedWeekHistories[queueKey] &&
                    weekly.map((week) => {
                      const weekKey = `${queueKey}::w${week.weekIndex}`;
                      const isWeekOpen = !!expandedWeeks[weekKey];
                      const daysBySunSat = SUN_TO_SAT.map((dayName) =>
                        week.days.find((d) => d.weekday === dayName) ?? null
                      );
                      return (
                        <Fragment key={weekKey}>
                          {/* Week header row */}
                          <tr
                            className="border-t border-gray-100 cursor-pointer bg-white hover:bg-gray-50"
                            onClick={() =>
                              setExpandedWeeks((prev) => ({
                                ...prev,
                                [weekKey]: !prev[weekKey],
                              }))
                            }
                          >
                            <td className="px-4 py-1 pl-20 sticky left-0 bg-white z-10 border-r border-gray-100">
                              <div className="flex items-center gap-1.5">
                                {isWeekOpen ? (
                                  <ChevronDown className="h-3 w-3 text-gray-300 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="h-3 w-3 text-gray-300 flex-shrink-0" />
                                )}
                                <span className="text-xs text-gray-500">
                                  Week {week.weekIndex + 1} — {week.label}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600 border border-indigo-100">
                                  {Math.round(getWeekWeightPercent(week.weekIndex, weekly.length) * 100)}%
                                </span>
                                {(week.isEstimated || week.hasPartialEstimates) && (
                                  <span className="text-xs text-amber-500 ml-1">
                                    {week.isEstimated ? "(Est)" : "(Partial Est)"}
                                  </span>
                                )}
                              </div>
                            </td>
                            {daysBySunSat.map((day, i) => (
                              <td key={i} className="px-3 py-1 text-center text-xs text-gray-400">
                                {day ? day.callsReceived : <span className="text-gray-200">—</span>}
                              </td>
                            ))}
                          </tr>

                          {/* Metric rows when week expanded */}
                          {isWeekOpen && (
                            <>
                              <tr className="border-t border-gray-50 bg-white">
                                <td className="px-4 py-1 pl-24 sticky left-0 bg-white z-10 text-xs text-gray-400 border-r border-gray-100">
                                  Calls Received
                                </td>
                                {daysBySunSat.map((day, i) => (
                                  <td key={i} className="px-3 py-1 text-center text-xs text-gray-600">
                                    {day ? day.callsReceived : 0}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-t border-gray-50 bg-white">
                                <td className="px-4 py-1 pl-24 sticky left-0 bg-white z-10 text-xs text-gray-400 border-r border-gray-100">
                                  AHT (min)
                                </td>
                                {daysBySunSat.map((day, i) => (
                                  <td key={i} className="px-3 py-1 text-center text-xs text-gray-600">
                                    {day ? fmt2(day.ahtMinutes) : "—"}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-t border-gray-50 bg-white">
                                <td className="px-4 py-1 pl-24 sticky left-0 bg-white z-10 text-xs text-gray-400 border-r border-gray-100">
                                  PCC Hours
                                </td>
                                {daysBySunSat.map((day, i) => (
                                  <td key={i} className="px-3 py-1 text-center text-xs text-purple-600">
                                    {day ? fmt3(day.pccHours) : "—"}
                                  </td>
                                ))}
                              </tr>
                            </>
                          )}
                        </Fragment>
                      );
                    })}
                </>
              )}
            </Fragment>
          );
        })}
    </>
  );
}
